// Question Shuffler Utility
// Shuffles answer options and redistributes correct answers evenly

import type { Question } from '../types';
import { logger } from './logger';

/**
 * Shuffle array using Fisher-Yates algorithm
 */
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/**
 * Shuffle answer options for a single question
 * Returns new question with shuffled options and updated correctAnswerId
 */
export function shuffleQuestionOptions(question: Question): Question {
  if (!question.options || question.options.length === 0) {
    return question;
  }

  // Create a copy of options
  const originalOptions = [...question.options];
  
  // Find the original correct answer
  const correctOption = originalOptions.find(opt => opt.id === question.correctAnswerId);
  if (!correctOption) {
    logger.warn(`Correct answer not found for question ${question.id}, skipping shuffle`);
    return question;
  }

  // Shuffle options
  const shuffledOptions = shuffleArray(originalOptions);
  
  // Find new position of correct answer
  const newCorrectAnswerId = shuffledOptions.find(opt => opt.id === question.correctAnswerId)?.id;
  if (!newCorrectAnswerId) {
    logger.warn(`Could not find correct answer after shuffle for question ${question.id}`);
    return question;
  }

  // Preserve all question properties including imageUrl and imageHint
  return {
    ...question,
    options: shuffledOptions,
    correctAnswerId: newCorrectAnswerId,
    // Explicitly preserve image properties
    imageUrl: question.imageUrl,
    imageHint: question.imageHint
  };
}

/**
 * Redistribute correct answers evenly across A, B, C positions
 * Ensures correct answers are distributed as evenly as possible
 */
export function redistributeCorrectAnswers(questions: Question[]): Question[] {
  if (questions.length === 0) return questions;

  // Count current distribution
  const positionCounts = { A: 0, B: 0, C: 0 };
  
  questions.forEach(q => {
    if (q.options && q.options.length > 0) {
      const correctIndex = q.options.findIndex(opt => opt.id === q.correctAnswerId);
      if (correctIndex >= 0 && correctIndex < 3) {
        const letter = String.fromCharCode(65 + correctIndex); // A, B, or C
        if (letter === 'A') positionCounts.A++;
        else if (letter === 'B') positionCounts.B++;
        else if (letter === 'C') positionCounts.C++;
      }
    }
  });

  logger.debug('Current correct answer distribution:', positionCounts);

  // Target: equal distribution (or as close as possible)
  const targetPerPosition = Math.floor(questions.length / 3);
  const remainder = questions.length % 3;
  
  // Ideal distribution: targetPerPosition for each, with remainder distributed
  const idealDistribution = {
    A: targetPerPosition + (remainder >= 1 ? 1 : 0),
    B: targetPerPosition + (remainder >= 2 ? 1 : 0),
    C: targetPerPosition
  };

  logger.debug('Target correct answer distribution:', idealDistribution);

  // Redistribute questions
  const redistributed = questions.map((q, index) => {
    // Calculate which position this question should have (cycle through A, B, C)
    const positionIndex = index % 3; // 0 = A, 1 = B, 2 = C
    
    // Find current correct answer
    if (!q.options || q.options.length === 0) return q;
    
    const correctOption = q.options.find(opt => opt.id === q.correctAnswerId);
    if (!correctOption) return q;

    // Find current position of correct answer
    const currentIndex = q.options.findIndex(opt => opt.id === q.correctAnswerId);
    if (currentIndex < 0 || currentIndex >= q.options.length) return q;

    // If already in target position, no need to shuffle
    if (currentIndex === positionIndex) return q;

    // Shuffle options to move correct answer to target position
    const shuffledOptions = [...q.options];
    
    // Move correct option to target position
    const [correctOptionItem] = shuffledOptions.splice(currentIndex, 1);
    shuffledOptions.splice(positionIndex, 0, correctOptionItem);

    // Preserve all question properties including imageUrl and imageHint
    return {
      ...q,
      options: shuffledOptions,
      correctAnswerId: correctOptionItem.id,
      // Explicitly preserve image properties
      imageUrl: q.imageUrl,
      imageHint: q.imageHint
    };
  });

  // Verify new distribution
  const newPositionCounts = { A: 0, B: 0, C: 0 };
  redistributed.forEach(q => {
    if (q.options && q.options.length > 0) {
      const correctIndex = q.options.findIndex(opt => opt.id === q.correctAnswerId);
      if (correctIndex >= 0 && correctIndex < 3) {
        const letter = String.fromCharCode(65 + correctIndex);
        if (letter === 'A') newPositionCounts.A++;
        else if (letter === 'B') newPositionCounts.B++;
        else if (letter === 'C') newPositionCounts.C++;
      }
    }
  });

  logger.debug('New correct answer distribution:', newPositionCounts);

  return redistributed;
}

/**
 * Normalize a question so all required fields exist for safety checks.
 * Base English data uses `question` (not `text`) and `answerId` (not `correctAnswerId`).
 * Translated data uses `text` (not `question`). Safety checks expect all fields,
 * so we ensure every alias pair is cross-filled after normalization.
 */
function normalizeQuestion(q: Question): Question {
  const raw = q as any;
  return {
    ...q,
    text: q.text || raw.question || '',
    question: raw.question || q.text || '',
    correctAnswerId: q.correctAnswerId || q.answerId || '',
    answerId: q.answerId || q.correctAnswerId || '',
    topic: q.topic || q.subject || 'General',
  };
}

/**
 * Process questions: shuffle options and redistribute correct answers
 * This is called when loading questions or retaking a test
 *
 * IMPORTANT: Order matters!
 * 0. Normalize fields (ensure text + question both exist)
 * 1. First shuffle options (randomizes option positions)
 * 2. Then redistribute correct answers (ensures even A, B, C distribution)
 */
export function processQuestions(questions: Question[], shuffleOptions: boolean = true, redistributeAnswers: boolean = true): Question[] {
  if (questions.length === 0) return questions;

  // Step 0: Normalize — guarantee text + question fields exist
  let processed = questions.map(normalizeQuestion);

  // Step 1: Shuffle options for each question first (randomizes option order)
  if (shuffleOptions) {
    processed = processed.map(q => shuffleQuestionOptions(q));
  }

  // Step 2: Redistribute correct answers evenly (A, B, C) after shuffling
  // This ensures correct answers are evenly distributed regardless of shuffle result
  if (redistributeAnswers) {
    processed = redistributeCorrectAnswers(processed);
  }

  return processed;
}

